// CRITICAL: Sentry must be imported FIRST before any other code

// Extend Window with debug/analytics globals set in this file
declare global {
  interface Window {
    posthog: typeof import("posthog-js").default | undefined;
    posthogSafeCapture: ((eventName: string, properties?: Record<string, unknown>) => boolean) | undefined;
    isPostHogReady: (() => boolean) | undefined;
    cloudflareUnavailable: boolean | undefined;
    __TEMPLATE_CONFIG__: Record<string, unknown> | undefined;
    setRegion: ((region: "US" | "SPAIN" | "LATAM" | "OTHER") => void) | undefined;
    testCountry: ((countryCode: string) => void) | undefined;
    testCloudflareGeo: (() => Promise<void>) | undefined;
    checkCloudflareStatus: (() => Promise<void>) | undefined;
    resetCloudflareStatus: (() => void) | undefined;
    forceCloudflareTest: (() => Promise<void>) | undefined;
    testExternalGeo: (() => Promise<void>) | undefined;
    diagnoseCloudflare: (() => Promise<void>) | undefined;
    posthogDebug: (() => void) | undefined;
    posthogStartRecording: (() => void) | undefined;
    posthogStopRecording: (() => void) | undefined;
    posthogTestEvent: (() => void) | undefined;
    gtag: ((...args: unknown[]) => void) | undefined;
  }
}

import { BRAND_SITE_HOST, BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import {
  BRAND_POSTHOG_PROJECT_ID,
  BRAND_POSTHOG_UI_HOST,
  resolveClientPostHogProjectId,
} from "@/lib/posthogBrandAlignment";
import "./sentry.client.config";

import posthog from "posthog-js";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./i18n/config";
import { installSiteDebugGlobals } from "./lib/siteDebug";
import { initAnalyticsErrorHandler } from "./lib/analyticsErrorHandler";
import { initAnalyticsLoader } from "./lib/loadAnalytics";
import {
  POSTHOG_CLIENT_READY_EVENT,
  initPersonPropertiesPostHog,
  posthogBeforeSendEnrich,
  registerPostHogFeatureFlagsReadyListener,
  registerSuperPropertiesPostHog,
  trackErrorPostHog,
  trackWebVitalsPostHog,
} from "./lib/posthogAnalytics";
import { initTallyEmbedPostMessageTracking } from "./lib/tallyEmbedTracking";
import { prefetchSiteSettingsEarly } from "./hooks/useSiteSettings";

installSiteDebugGlobals();

// Start site-settings ASAP (before React tree) — pairs with <link rel=preload> in index.html.
// Critical on AR 4G / Instagram WebView so Mux templates don't wait on cold fetch.
try {
  prefetchSiteSettingsEarly("live");
} catch {
  /* ignore */
}

// Initialize analytics error handler to silence blocked analytics errors
initAnalyticsErrorHandler();

// Initialize lazy loading of third-party analytics (GA, Meta Pixel, Metricool, Tally)
// These load after user interaction or 4s idle for better FCP/LCP
initAnalyticsLoader();

// PostHog + Session Replay: mismo SDK que el snippet del dashboard, vía `posthog-js` (no dupliques el <script> en HTML).

// Import debug utilities in development
if (import.meta.env.DEV) {
  try {
    import("./utils/debugCountryTemplates");
  } catch {
    // intentional
  }
}

// Support both NEXT_PUBLIC_ (Next.js convention) and VITE_ (Vite convention) env vars
const posthogKey = 
  import.meta.env.NEXT_PUBLIC_POSTHOG_KEY || 
  import.meta.env.VITE_POSTHOG_KEY || 
  "";
// Browser: first-party proxy `/ingest` (ad-blocker safe). Server capture uses POSTHOG_HOST.
const posthogHost =
  import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ||
  import.meta.env.VITE_POSTHOG_HOST ||
  "/ingest";

/** UI host for session replay deep-links — Railway VITE_POSTHOG_UI_HOST, never /ingest. */
const posthogUiHost = (() => {
  const raw = String(
    import.meta.env.VITE_POSTHOG_UI_HOST ||
      import.meta.env.NEXT_PUBLIC_POSTHOG_UI_HOST ||
      BRAND_POSTHOG_UI_HOST,
  )
    .trim()
    .replace(/\/+$/, "");
  if (!raw || raw.startsWith("/") || raw.includes("i.posthog") || raw.includes("/ingest")) {
    return BRAND_POSTHOG_UI_HOST;
  }
  return raw;
})();

/** Numeric project id — brand canonical **422815** (Ana Fernandez). */
const posthogProjectId = resolveClientPostHogProjectId() || BRAND_POSTHOG_PROJECT_ID;

/** Session replay sample 0–1 (default 1). Set VITE_POSTHOG_REPLAY_SAMPLE_RATE=0.25 in Railway to save quota. */
function readPostHogReplaySampleRate(): number {
  const raw =
    import.meta.env.VITE_POSTHOG_REPLAY_SAMPLE_RATE ||
    import.meta.env.NEXT_PUBLIC_POSTHOG_REPLAY_SAMPLE_RATE;
  if (raw === undefined || String(raw).trim() === "") return 1;
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n) || n < 0 || n > 1) return 1;
  return n;
}

const posthogReplaySampleRate = readPostHogReplaySampleRate();

if (typeof posthogKey === "string" && posthogKey.length > 0) {
  try {
    // Ensure PostHog is available on window immediately
    if (typeof window !== "undefined") {
      window.posthog = posthog;
    }
    
    posthog.init(posthogKey, {
      api_host: posthogHost,
      // Critical with reverse proxy (`/ingest`): without ui_host, get_session_replay_url()
      // returns `/ingest/project/phc_…/replay/…` (404). From Railway VITE_POSTHOG_UI_HOST.
      ui_host: posthogUiHost,
      // Alineado con el snippet del proyecto en PostHog (US). Para perfiles de anónimos usá 'always'.
      person_profiles: "identified_only",
      // Defaults configuration (PostHog project “defaults” date)
      defaults: "2026-01-30",
      /** Tag client for flag evaluation + HogQL (startup / MCP workflows). */
      evaluation_contexts: [
        "web",
        import.meta.env.PROD ? "production" : "development",
        `project_${posthogProjectId}`,
      ],
      /** Correlation + environment on every event (no extra capture() calls). Saves manual props in product code. */
      before_send: posthogBeforeSendEnrich,
      // SPA: we capture pageviews on route changes via <PostHogPageview />
      capture_pageview: false,
      capture_pageleave: true,
      // Session recording - enabled for ALL templates (BaseTemplate, BioLinkTemplate, ArgentinaBioTemplate)
      session_recording: {
        enabled: true,
        record_canvas: false, // Disable canvas recording for performance
        record_cross_origin_iframes: false, // Disable cross-origin iframe recording
        // Prod: off (menos ruido y peso en replays); dev: on para depurar
        capture_console_logs: Boolean(import.meta.env.DEV),
        // Network request capture - enabled as per PostHog project settings
        // Note: recordHeaders and recordBody are configured in PostHog project settings
        // The dashboard settings you activated will control headers/body capture
        capture_network_requests: true, // Enable network request capture
        // Custom function to mask sensitive network data
        // This ensures sensitive headers and payloads are redacted even if dashboard settings change
        maskCapturedNetworkRequestFn: (request: {
          url?: string; name?: string;
          requestHeaders?: Headers; responseHeaders?: Headers;
          requestBody?: string; responseBody?: string;
        } | null) => {
          if (!request) return null;

          // Skip analytics/telemetry endpoints — they're not useful in session recordings
          // and cause CORS errors in the console when PostHog tries to re-capture them
          const url = request.url || request.name || "";
          const skipUrls = [
            "axiom.co",
            "inferred.litix.io",    // Mux analytics
            "posthog.com",          // PostHog itself
            "us.i.posthog.com",
            "eu.i.posthog.com",
          ];
          if (skipUrls.some((s) => url.includes(s))) return null;
          
          // List of sensitive headers to mask
          const sensitiveHeaders = [
            'authorization',
            'x-api-key',
            'api-key',
            'x-auth-token',
            'cookie',
            'set-cookie',
            'x-csrf-token',
            'csrf-token',
            'x-session-id',
            'session-id',
            'x-access-token',
            'access-token',
            'x-refresh-token',
            'refresh-token',
            'stripe-signature',
            'x-stripe-signature',
          ];
          
          // Mask sensitive headers
          if (request.requestHeaders) {
            sensitiveHeaders.forEach(header => {
              const lowerHeader = header.toLowerCase();
              if (request.requestHeaders.has(lowerHeader)) {
                request.requestHeaders.set(lowerHeader, '[REDACTED]');
              }
            });
          }
          
          if (request.responseHeaders) {
            sensitiveHeaders.forEach(header => {
              const lowerHeader = header.toLowerCase();
              if (request.responseHeaders.has(lowerHeader)) {
                request.responseHeaders.set(lowerHeader, '[REDACTED]');
              }
            });
          }
          
          // Mask sensitive data in request body
          if (request.requestBody && typeof request.requestBody === 'string') {
            try {
              const body = JSON.parse(request.requestBody);
              const sensitiveFields = [
                'password', 'password_confirmation', 'current_password', 'new_password',
                'token', 'api_key', 'apiKey', 'access_token', 'refresh_token',
                'secret', 'secret_key', 'private_key', 'credit_card', 'card_number',
                'cvv', 'cvc', 'card_cvv', 'card_cvc', 'ssn', 'social_security_number',
                'stripe_token', 'stripeToken',
              ];
              
              sensitiveFields.forEach(field => {
                if (body[field]) {
                  body[field] = '[REDACTED]';
                }
              });
              
              request.requestBody = JSON.stringify(body);
            } catch (e) {
              // If body is not JSON, check for sensitive patterns
              const sensitivePatterns = [
                /"password"\s*:\s*"[^"]*"/gi,
                /"token"\s*:\s*"[^"]*"/gi,
                /"api[_-]?key"\s*:\s*"[^"]*"/gi,
                /"secret"\s*:\s*"[^"]*"/gi,
              ];
              
              sensitivePatterns.forEach(pattern => {
                request.requestBody = request.requestBody.replace(pattern, (match: string) => {
                  return match.replace(/:\s*"[^"]*"/, ': "[REDACTED]"');
                });
              });
            }
          }
          
          // Offer validation gap-close: keep a *sanitized* resolve-offer body so
          // session replays show bucket/code/percent without tokens. Other APIs
          // still redact sensitive fields.
          const isKimiOfferApi =
            url.includes("/api/kimi/resolve-offer") ||
            url.includes("/api/kimi/stamp-outcome");

          // Mask sensitive data in response body
          if (request.responseBody && typeof request.responseBody === 'string') {
            try {
              const body = JSON.parse(request.responseBody);
              if (isKimiOfferApi && body && typeof body === "object") {
                // Explicit allow-list for ops validation in replay network panel
                const b = body as Record<string, unknown>;
                request.responseBody = JSON.stringify({
                  ok: b.ok,
                  kimi_decision_id: b.kimi_decision_id,
                  kimi_bucket: b.kimi_bucket,
                  kimi_tier: b.kimi_tier,
                  kimi_score: b.kimi_score,
                  percent_off: b.percent_off,
                  promotion_code: b.promotion_code,
                  promotion_code_id: b.promotion_code_id
                    ? "[promo_id]"
                    : null,
                  source: b.source,
                  pattern_label: b.pattern_label,
                  billing_plan: b.billing_plan,
                  interval_count: b.interval_count,
                  fallback_reason: b.fallback_reason,
                  outcome: b.outcome,
                  stamped: b.stamped,
                });
              } else {
                const sensitiveFields = ['token', 'access_token', 'refresh_token', 'api_key', 'secret'];
                
                sensitiveFields.forEach(field => {
                  if (body[field]) {
                    body[field] = '[REDACTED]';
                  }
                });
                
                request.responseBody = JSON.stringify(body);
              }
            } catch (e) {
              // If body is not JSON, apply pattern matching
              const sensitivePatterns = [
                /"token"\s*:\s*"[^"]*"/gi,
                /"api[_-]?key"\s*:\s*"[^"]*"/gi,
              ];
              
              sensitivePatterns.forEach(pattern => {
                request.responseBody = request.responseBody.replace(pattern, (match: string) => {
                  return match.replace(/:\s*"[^"]*"/, ': "[REDACTED]"');
                });
              });
            }
          }
          
          return request;
        },
        sample_rate: posthogReplaySampleRate,
        minimum_duration: 0, // Record all sessions regardless of duration
        // Mask sensitive data
        mask_all_inputs: true, // Mask all input fields for privacy
        mask_text_selector: '[data-ph-mask]', // Custom selector for masking
        // Performance optimizations
        record_canvas_fps: 2, // Low FPS for canvas if enabled
      },
      // Autocapture - enabled to track all events on all templates
      autocapture: {
        enabled: true, // Enable autocapture to track all interactions
        css_selector_allowlist: ["[data-attr]", "button", "a", "[data-ph-capture]"], // Capture buttons, links, and elements with data attributes
        url_allowlist: [], // Capture on all URLs (empty = all URLs)
        dom_event_allowlist: ["click", "change", "submit", "input"], // Capture clicks, form changes, and input events
        // Evita subir portapapeles a PostHog (privacidad + volumen)
        capture_copied_text: false,
        // Capture all interactive elements
        capture_rage_clicks: true, // Capture rage clicks
        capture_dead_clicks: true, // Capture dead clicks
      },
      // Enable feature flags
      bootstrap: {},
      loaded: (posthogInstance) => {
        // Ensure PostHog is available globally
        if (typeof window !== "undefined") {
          window.posthog = posthogInstance;
        }
        
        // Debug mode in development
        if (import.meta.env.DEV) {
          posthogInstance.debug(true);
        }

        // Register super properties — auto-attached to every future event
        registerSuperPropertiesPostHog();

        // Enrich person profile with custom properties + session count
        initPersonPropertiesPostHog();

        // Observe Core Web Vitals (LCP, FCP, CLS, TTFB, INP)
        trackWebVitalsPostHog();

        registerPostHogFeatureFlagsReadyListener(posthogInstance);

        // Global error tracking — wire up unhandled JS errors and promise rejections
        window.addEventListener("error", (event) => {
          const error = event.error instanceof Error
            ? event.error
            : new Error(event.message || "Unknown error");
          trackErrorPostHog(error, {
            error_type: "uncaught_exception",
            source: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          });
        });

        window.addEventListener("unhandledrejection", (event) => {
          const error = event.reason instanceof Error
            ? event.reason
            : new Error(String(event.reason ?? "Unhandled rejection"));
          trackErrorPostHog(error, { error_type: "unhandled_rejection" });
        });
        
        // Solo con diagnóstico explícito: evita inflar el proyecto PostHog compartido
        // cuando muchos `npm run dev` apuntan a la misma key (ver HogQL / SDK Doctor en MCP).
        if (import.meta.env.DEV) {
          try {
            const phDebug =
              typeof window !== "undefined" &&
              (localStorage.getItem("ph_debug") === "1" ||
                new URLSearchParams(window.location.search).get("ph_debug") === "1");
            if (phDebug) {
              posthogInstance.capture("posthog_initialized", {
                timestamp: new Date().toISOString(),
                sdk_version: posthogInstance.version,
                autocapture_enabled:
                  posthogInstance.config?.autocapture?.enabled || false,
                session_recording_enabled:
                  posthogInstance.config?.session_recording?.enabled || false,
                environment: import.meta.env.MODE || "development",
              });
            }
          } catch {
            // silenced
          }
        }

        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent(POSTHOG_CLIENT_READY_EVENT));
        }
      },
      // Privacy settings
      respect_dnt: true, // Respect Do Not Track
      opt_out_capturing_by_default: false,
      opt_out_persistence_by_default: false,
      // Performance settings
      batch_requests: true,
      request_batching: true,
      // Cross-domain tracking
      cross_subdomain_cookie: false,
      secure_cookie: true,
      // Persistence
      persistence: "localStorage+cookie",
      persistence_name: `ph_${BRAND_SOURCE_KEY}`,
      // Advanced settings
      property_blacklist: [], // Properties to exclude
      sanitize_properties: null, // Function to sanitize properties
      xhr_headers: {}, // Custom headers for requests
      // Error handling
      on_xhr_error: (_failedRequest) => {
        if (import.meta.env.DEV) {
          // intentional: dev-only error logging placeholder
        }
      },
    });
  } catch (error) {
    // Continue without PostHog if it fails (likely blocked by adblocker)
    // Error is already silenced by analyticsErrorHandler
    if (import.meta.env.DEV) {
      console.debug("PostHog bloqueado por el usuario o error de inicialización");
    }
  }
} else {
  // Log when PostHog is not configured
  if (import.meta.env.DEV) {
    console.warn("PostHog not configured - NEXT_PUBLIC_POSTHOG_KEY or VITE_POSTHOG_KEY not found, continuing without analytics");
  }
  
  // Ensure window.posthog is undefined when not configured
  if (typeof window !== "undefined") {
    window.posthog = undefined;
  }
}

// Always make safe capture function available, even if PostHog is not configured
if (typeof window !== "undefined") {
  // Safe PostHog capture helper - checks if PostHog is ready before capturing
  window.posthogSafeCapture = (eventName: string, properties?: Record<string, unknown>) => {
    if (typeof window === 'undefined') {
      return false;
    }
    
    const posthog = window.posthog;
    if (!posthog) {
      return false;
    }
    
    if (typeof posthog.capture !== 'function') {
      return false;
    }
    
    try {
      posthog.capture(eventName, properties);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Helper to check if PostHog is ready
  window.isPostHogReady = () => {
    if (typeof window === 'undefined') return false;
    const posthog = window.posthog;
    return !!(posthog && typeof posthog.capture === 'function' && posthog.__loaded);
  };

  initTallyEmbedPostMessageTracking();
}

// Helper functions for region and country testing (no copy/paste needed!)
if (typeof window !== "undefined") {
  window.setRegion = (region: "US" | "SPAIN" | "LATAM" | "OTHER") => {
    localStorage.setItem("user_region", region);
    localStorage.removeItem("test_country"); // Clear country test
    window.location.reload();
  };

  window.testCountry = (countryCode: string) => {
    localStorage.setItem("user_region", "LATAM");
    localStorage.setItem("test_country", countryCode);
    // Simulate browser language
    Object.defineProperty(navigator, "language", {
      writable: true,
      value: `es-${countryCode}`,
    });
    window.location.reload();
  };

  // Test Cloudflare Enterprise geolocation (PRIMARY METHOD)
  window.testCloudflareGeo = async () => {
    if (!import.meta.env.DEV) return;
    console.log(`🚀 Testing Cloudflare Enterprise geolocation (PRIMARY for ${BRAND_SITE_HOST})...`);
    console.log("   Cloudflare marked as unavailable:", !!window.cloudflareUnavailable);
    console.log("📍 Template configuration is injected by Cloudflare Worker");
    console.log("🔧 Check window.__TEMPLATE_CONFIG__ for current configuration");
    if (window.__TEMPLATE_CONFIG__) {
      console.log("Current config:", window.__TEMPLATE_CONFIG__);
    }
  };

  // Check Cloudflare status
  window.checkCloudflareStatus = async () => {
    try {
      const response = await fetch("/api/cloudflare-status");
      const status = await response.json();
      if (import.meta.env.DEV) {
        console.log("   - Behind Cloudflare:", status.cloudflare.present ? "✅ YES" : "❌ NO");
        console.log("   - Geolocation Headers:", status.geolocation.hasCountry ? "✅ YES" : "❌ NO");
        console.log("   - True-Client-IP:", status.cloudflare.hasTrueClientIp ? "✅ YES" : "❌ NO");
      }
      if (!status.cloudflare.present && import.meta.env.DEV) {
        console.log("   Solution: Configure domain in Cloudflare and update DNS");
      } else if (!status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("   Solution: Enable 'Add visitor location headers' in Cloudflare Managed Transforms");
      } else if (status.cloudflare.present && status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("✅ SUCCESS: Cloudflare and geolocation are properly configured!");
      }
    } catch {
      // intentional
    }
  };

  // Reset Cloudflare availability status
  window.resetCloudflareStatus = () => {
    window.cloudflareUnavailable = false;
    if (import.meta.env.DEV) {
      console.log("🔄 Cloudflare availability status reset - will try again on next detection");
    }
  };

  // Force Cloudflare test (ignore unavailable flag)
  window.forceCloudflareTest = async () => {
    const wasUnavailable = window.cloudflareUnavailable;
    window.cloudflareUnavailable = false;

    await window.testCloudflareGeo();

    // Restore the flag
    window.cloudflareUnavailable = wasUnavailable;
  };

  // Test external geolocation API (EMERGENCY BACKUP ONLY)
  window.testExternalGeo = async () => {
    if (import.meta.env.DEV) {
      console.log(`🚨 Testing EXTERNAL geolocation API (should NOT be primary for ${BRAND_SITE_HOST})...`);
      console.log("   ⚠️  This uses paid/limited APIs - Cloudflare Enterprise should handle this");
    }
    try {
      const response = await fetch("/api/geolocate");
      const geoData = await response.json();
      if (geoData.country) {
        if (import.meta.env.DEV) console.log("   ⚠️  WARNING: Using external API instead of Cloudflare Enterprise");
        const { getTemplateForCountry } = await import("@/config/countryTemplates");
        getTemplateForCountry(geoData.country);
      } else if (import.meta.env.DEV) {
        console.log("   💡 This means NO geolocation is working - check all setups");
      }
    } catch {
      // intentional
    }
  };

  // Quick Cloudflare diagnosis
  window.diagnoseCloudflare = async () => {
    try {
      const statusResponse = await fetch("/api/cloudflare-status");
      const status = await statusResponse.json();
      if (import.meta.env.DEV) {
        console.log("   Result:", status.cloudflare.present ? "✅ CLOUDFLARE ACTIVE" : "❌ NO CLOUDFLARE");
        console.log("   CF-RAY present:", status.cloudflare.hasCfRay ? "✅ YES" : "❌ NO");
        console.log("   True-Client-IP:", status.cloudflare.hasTrueClientIp ? "✅ YES" : "❌ NO");
      }
      if (!status.cloudflare.present && import.meta.env.DEV) {
        console.log("   💡 SOLUTION: Check DNS nameservers point to Cloudflare");
        console.log("   🔗 HOW TO: CLOUDFLARE_DIAGNOSIS.md section 'Agregar Dominio a Cloudflare'");
      } else if (status.cloudflare.present && !status.geolocation.hasCountry && import.meta.env.DEV) {
        console.log("   ⚠️  WARNING: Cloudflare active but no geolocation headers!");
        console.log("   💡 SOLUTION: Enable 'Add visitor location headers' in Managed Transforms");
        console.log("   🔗 HOW TO: CLOUDFLARE_DIAGNOSIS.md section 'Configuración de Zone Settings'");
      }
    } catch {
      // intentional
    }
    try {
      const testResponse = await fetch(window.location.origin, { method: "HEAD" });
      const cfRay = testResponse.headers.get("cf-ray");
      if (import.meta.env.DEV && cfRay) console.log("   CF-RAY:", cfRay);
    } catch {
      // intentional
    }
    // Geolocation now handled by server via headers
    if (import.meta.env.DEV) {
      console.log("🎯 If CF-RAY exists but no country: Enable 'Add visitor location headers'");
    }
  };

  // Safe PostHog debug helpers
  window.posthogDebug = () => {
    try {
      const posthogKey = import.meta.env.NEXT_PUBLIC_POSTHOG_KEY || import.meta.env.VITE_POSTHOG_KEY || "";
      const posthogHost = import.meta.env.NEXT_PUBLIC_POSTHOG_HOST || import.meta.env.VITE_POSTHOG_HOST || "/ingest";
      if (import.meta.env.DEV) {
        console.log("PostHog key:", import.meta.env.NEXT_PUBLIC_POSTHOG_KEY ? "NEXT_PUBLIC_POSTHOG_KEY" : import.meta.env.VITE_POSTHOG_KEY ? "VITE_POSTHOG_KEY" : "Not found");
        console.log("PostHog host:", import.meta.env.NEXT_PUBLIC_POSTHOG_HOST ? "NEXT_PUBLIC_POSTHOG_HOST" : import.meta.env.VITE_POSTHOG_HOST ? "VITE_POSTHOG_HOST" : "Default");
      }
      const posthog = window.posthog;
      if (posthog && import.meta.env.DEV) {
        try {
          console.log("PostHog distinctId:", posthog.get_distinct_id?.());
        } catch {
          // intentional
        }
      }
    } catch {
      // intentional
    }
  };

  window.posthogStartRecording = () => {
    try {
      const posthog = window.posthog;
      if (posthog?.startSessionRecording) {
        posthog.startSessionRecording();
      }
    } catch {
      // intentional
    }
  };

  window.posthogStopRecording = () => {
    try {
      const posthog = window.posthog;
      if (posthog?.stopSessionRecording) {
        posthog.stopSessionRecording();
      }
    } catch {
      // intentional
    }
  };

  window.posthogTestEvent = () => {
    try {
      if (window.posthogSafeCapture) {
        window.posthogSafeCapture("test_event", {
          source: "console_command",
          timestamp: new Date().toISOString(),
          user_region: localStorage.getItem("user_region") || "LATAM",
        });
      }
    } catch {
      // intentional
    }
  };

  if (import.meta.env.DEV) {
    console.log("%c📍 Region & Country Helpers Available!", "color: #667eea; font-size: 14px; font-weight: bold");
    console.log("🌍 Regions: setRegion('US') | setRegion('SPAIN') | setRegion('LATAM')");
    console.log("🌎 LATAM Countries: testCountry('MX') | testCountry('CL') | testCountry('PY') | testCountry('BR')");
    console.log(`🚀 Cloudflare Enterprise: testCloudflareGeo() - PRIMARY geolocation (${BRAND_SITE_HOST})`);
    console.log("🔍 Cloudflare: checkCloudflareStatus() - Check Cloudflare Enterprise setup");
    console.log("🔄 Cloudflare: resetCloudflareStatus() - Reset Cloudflare availability");
    console.log("🔧 Cloudflare: forceCloudflareTest() - Force test (debug only)");
    console.log("🚨 External: testExternalGeo() - EMERGENCY backup (paid/limited APIs)");
    console.log("📊 Templates: Worker home = BaseTemplate (todos los países; ver worker.js COUNTRY_TEMPLATE_MAP)");
    console.log("🎯 PostHog: posthogDebug() | posthogStartRecording() | posthogStopRecording() | posthogTestEvent()");
    console.log("   ph_debug: localStorage.setItem('ph_debug','1') or ?ph_debug=1 → emite posthog_initialized al cargar el SDK");
    console.log("📊 PostHog Safe Capture: posthogSafeCapture('event_name', { property: 'value' }) - Safe way to capture events");
    console.log("Example: testCountry('MX') - Tests Mexico (BaseTemplate + idioma es-MX)");
    console.log("Example: posthogDebug() - Shows PostHog status and debug info");
  }
}

// Render function - ensures DOM is ready
const renderApp = () => {

  try {
    const rootElement = document.getElementById("root");
    if (!rootElement) {
      console.error("❌ Root element not found - cannot render React application");
      document.body.innerHTML =
        '<div style="padding: 20px; color: red;"><h1>Root element not found</h1></div>';
      return;
    }


    const root = createRoot(rootElement);

    root.render(<App />);
  } catch (error) {
    const errMsg = error instanceof Error ? error.message : "Unknown error";
    const errName = error instanceof Error ? error.name : "Error";
    // Try to show a basic error message
    try {
      const rootElement = document.getElementById("root");
      if (rootElement) {
        rootElement.innerHTML = `
          <div style="padding: 20px; font-family: Arial, sans-serif; color: red; border: 2px solid red;">
            <h1>🚨 Error Loading Application</h1>
            <p><strong>Error:</strong> ${errMsg}</p>
            <p><strong>Type:</strong> ${errName}</p>
            <p>Please check the browser console for more details.</p>
            <button onclick="window.location.reload()" style="padding: 10px; background: red; color: white; border: none; border-radius: 5px; cursor: pointer;">Refresh Page</button>
          </div>
        `;
      } else {
        document.body.innerHTML =
          '<div style="padding: 20px; color: red;"><h1>Critical error and root element not found</h1></div>';
      }
    } catch {
      // intentional
    }
  }
};

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", renderApp);
} else {
  // DOM is already ready, render immediately
  renderApp();
}
